"use client";

import { format } from "date-fns";
import Link from "next/link";
import { useSearchParams } from "next/navigation";

import { AccessCheck } from "@/components/access-check";
import { ClientOnly } from "@/components/client-only";
import { ExaminePlusLink } from "@/components/cta";
import parse from "@/components/html-parser";
import { LockBadgeOverlay, SSOverlayCta } from "@/components/lock";
import NewsletterOptin from "@/components/newsletter-optin";
import { StudySummaryActionBar } from "@/components/study-summary-action";
import { Alert } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { percentOff, saleLabel } from "@/lib/data/sales";
import { User } from "@/types";

import { FreeTrialWrapper } from "./free-trial-wrapper";
import SaleWrapper from "./sale-wrapper";

interface SummaryDataProps {
	user: User;
	// TEMPORARY
	studyData: any;
}

type Citation = {
	authors: string;
	title: string;
	journal: string;
	published: string;
	url: string;
	ref_id?: string;
};

const Citation = ({ citation }: { citation: Citation }) => {
	if (!citation) return null;

	return (
		<div>
			{citation.ref_id ? (
				<a href={`#ref-link-${citation.ref_id}`} className="px-2 text-primary underline">
					^
				</a>
			) : null}
			<span className="pr-2">{citation.authors}</span>
			<a
				className="text-primary hover:underline"
				href={citation.url}
				target="_blank"
				rel="noreferrer"
			>
				{citation.title}
			</a>
			<span className="px-2">{citation.journal}.</span>
			<span>({citation.published})</span>
		</div>
	);
};

const CitationSection = ({ citation }: { citation: Citation }) => {
	if (!citation) return null;

	return (
		<>
			<h2 className="mb-4 text-lg font-bold xl:text-xl">Citation</h2>
			<Citation citation={citation} />
		</>
	);
};

export default function StudyData({ user, studyData }: SummaryDataProps) {
	const searchParams = useSearchParams();
	const {
		headline,
		created_date,
		categories,
		id,
		v2,
		format: version,
		summary: { blurb, additional, study, results, bigpicture, background, study_who },
		references = [],
		citation,
	} = studyData;

	function replaceWordsWithSameLength(paragraph: string) {
		const words = paragraph.split(" ");

		const randomWords = ["a", "oy", "qub", "yoxi", "dizex", "plifex", "glerxan", "quixolan"];

		const replacedParagraph = words.map((word) => {
			const replacementWord = randomWords.find((randomWord) => randomWord.length === word.length);

			return replacementWord ? replacementWord : word;
		});

		return replacedParagraph.join(" ");
	}

	const requirelogin = searchParams.get("requirelogin");

	return (
		<ClientOnly>
			{version === "v3" ? (
				<div className="max-w-4xl space-y-7 bg-white pt-4 lg:pt-7 xl:pb-24">
					{blurb && (
						<div className="text-content space-y-4">
							<h2 className="mb-4 text-lg font-bold xl:text-xl">Quick Summary</h2>
							<div className="content">{parse(blurb)}</div>
						</div>
					)}

					{requirelogin && !user ? (
						<div className="relative xl:text-xl xl:leading-9">
							<LockBadgeOverlay
								size={10}
								text={<SSOverlayCta />}
								className="!top-[10%] w-full !translate-y-0 [@media(min-width:500px)]:w-auto"
							/>
							<div className="pointer-events-none select-none space-y-7 blur-sm">
								{study && (
									<div className="text-content space-y-4">
										<h2 className="mb-4 text-lg font-bold xl:text-xl">Key study details</h2>
										<div className="content">{parse(replaceWordsWithSameLength(study))}</div>
									</div>
								)}
								{/* The big picture */}
								{bigpicture && (
									<div className="text-content space-y-4">
										<h2 className="mb-4 text-lg font-bold xl:text-xl">The big picture</h2>
										<div className="content">{parse(replaceWordsWithSameLength(bigpicture))}</div>
									</div>
								)}
								{/* The results */}
								{results && (
									<div className="text-content space-y-4">
										<h2 className="mb-4 text-lg font-bold xl:text-xl">Our take</h2>
										<div className="content">{parse(replaceWordsWithSameLength(results))}</div>
									</div>
								)}
							</div>
						</div>
					) : (
						<>
							{study && (
								<div className="text-content space-y-4">
									<h2 className="mb-4 text-lg font-bold xl:text-xl">Key study details</h2>
									<div className="content">{parse(study)}</div>
								</div>
							)}
							{/* The big picture */}
							{bigpicture && (
								<div className="text-content space-y-4">
									<h2 className="mb-4 text-lg font-bold xl:text-xl">The big picture</h2>
									<div className="content">{parse(bigpicture)}</div>
								</div>
							)}
							{/* The results */}
							{results && (
								<div className="text-content space-y-4">
									<h2 className="mb-4 text-lg font-bold xl:text-xl">Our take</h2>
									<div className="content">{parse(results)}</div>
								</div>
							)}
						</>
					)}

					<AccessCheck feature={["study_summaries"]}>
						<Alert variant="premium">
							Every month we summarize over 150 of the most noteworthy health and nutrition studies.
							Other health categories related to this summary include:
							<ul className="list-disc py-4 pl-5 sm:pl-10">
								{categories?.map(
									(item: {
										id: React.Key;
										slug: any;
										name:
											| string
											| number
											| boolean
											| React.ReactElement<any, string | React.JSXElementConstructor<any>>
											| Iterable<React.ReactNode>
											| React.ReactPortal;
										tooltip: string;
									}) => (
										<li key={item.id}>
											<Link href={`/research-feed/filter/?filter=categories&value=${item.slug}`}>
												<span>
													<Tooltip>
														<TooltipTrigger asChild>
															<p className="inline-block text-examine-purple-400 hover:underline">
																{item.name}
															</p>
														</TooltipTrigger>
														<TooltipContent
															variant="overview"
															className="[@media(max-width:768px)]:hidden"
														>
															{parse(item.tooltip)}
														</TooltipContent>
													</Tooltip>
												</span>
											</Link>
										</li>
									)
								)}
							</ul>
							<SaleWrapper
								cta={
									<FreeTrialWrapper>
										<ExaminePlusLink
											trackDetails={{
												trackFor: "trial",
												label: "Try Examine+ for free",
												location: "SS - bottom",
											}}
											className="text-examine-purple-400 hover:underline"
										>
											Try Examine+ for free
										</ExaminePlusLink>
									</FreeTrialWrapper>
								}
							>
								<ExaminePlusLink
									trackDetails={{
										trackFor: "sale",
										label: `Get Examine+ for ${percentOff} off during our ${saleLabel}`,
										location: "SS - bottom",
									}}
									className="text-examine-purple-400 hover:underline"
								>
									Get Examine+ for {percentOff} off during our {saleLabel}
								</ExaminePlusLink>
							</SaleWrapper>{" "}
							to view the latest research in 26 health categories and the entire Study Summaries
							archive, access our{" "}
							<Link href="/guides/" className="text-examine-purple-400 hover:underline ">
								Supplement Guides
							</Link>
							, and unlock the Examine Database. Plus, earn continuing education credits!
							{!user ? (
								<>
									<br />
									<br />
									<NewsletterOptin>
										<>
											<strong>
												<a className="cursor-pointer text-examine-purple-400 hover:underline">
													Get free weekly updates on what’s new at Examine
												</a>
											</strong>
											.
										</>
									</NewsletterOptin>
								</>
							) : (
								""
							)}
						</Alert>
					</AccessCheck>

					<p className="mt-5 italic">
						This Study Summary was published on{" "}
						{created_date && format(new Date(created_date), "MMMM d',' yyyy")}.
					</p>

					<CitationSection citation={citation} />

					{Object.entries(references).length ? (
						<div id="references">
							<h2 className="mb-4 text-lg font-bold xl:text-xl">References</h2>
							<ol className="relative ml-5 w-full list-decimal space-y-6 py-4">
								{Object.entries(references)?.map(([id, value]: any, idx) => (
									<li key={idx} id={`ref-${value.ref_id}`} className="scroll-mt-28">
										<Citation citation={value} />
									</li>
								))}
							</ol>
						</div>
					) : null}
					<StudySummaryActionBar
						showFavorite={!!user}
						headline={headline}
						id={id}
						isFavorite={false}
					/>
				</div>
			) : v2 || version === "v2" ? (
				<div className="max-w-4xl space-y-7 bg-white pt-4 lg:pt-7 xl:pb-24">
					{blurb && (
						<div className="text-content space-y-4">
							<h2 className="mb-4 text-lg font-bold xl:text-xl">Quick Summary</h2>
							<div className="content">{parse(blurb)}</div>
						</div>
					)}
					{background && (
						<div className="text-content space-y-4">
							<h2 className="mb-4 text-lg font-bold xl:text-xl">What was studied?</h2>
							<div className="content">{parse(background)}</div>
						</div>
					)}

					{requirelogin && !user ? (
						<div className="relative xl:text-xl xl:leading-9">
							<LockBadgeOverlay
								size={10}
								text={<SSOverlayCta />}
								className="!top-[10%] w-full !translate-y-0 [@media(min-width:500px)]:w-auto"
							/>
							<div className="pointer-events-none select-none space-y-7 blur-sm">
								{study_who && (
									<div className="text-content space-y-4">
										<h2 className="mb-4 text-lg font-bold xl:text-xl">Who was studied?</h2>
										<div className="content">{parse(replaceWordsWithSameLength(study_who))}</div>
									</div>
								)}
								{study && (
									<div className="text-content space-y-4">
										<h2 className="mb-4 text-lg font-bold xl:text-xl">How was it studied?</h2>
										<div className="content">{parse(replaceWordsWithSameLength(study))}</div>
									</div>
								)}
								{/* The results */}
								{results && (
									<div className="text-content space-y-4">
										<h2 className="mb-4 text-lg font-bold xl:text-xl">What were the results?</h2>
										<div className="content">{parse(replaceWordsWithSameLength(results))}</div>
									</div>
								)}
								{/* The big picture */}
								{bigpicture && (
									<div className="text-content space-y-4">
										<h2 className="mb-4 text-lg font-bold xl:text-xl">The big picture</h2>
										<div className="content">{parse(replaceWordsWithSameLength(bigpicture))}</div>
									</div>
								)}

								{additional && (
									<div className="text-content space-y-4">
										<h2 className="mb-4 text-lg font-bold xl:text-xl">
											Anything else I need to know?
										</h2>
										<div className="content">{parse(replaceWordsWithSameLength(additional))}</div>
									</div>
								)}
							</div>
						</div>
					) : (
						<>
							{study_who && (
								<div className="text-content space-y-4">
									<h2 className="mb-4 text-lg font-bold xl:text-xl">Who was studied?</h2>
									<div className="content">{parse(study_who)}</div>
								</div>
							)}
							{study && (
								<div className="text-content space-y-4">
									<h2 className="mb-4 text-lg font-bold xl:text-xl">How was it studied?</h2>
									<div className="content">{parse(study)}</div>
								</div>
							)}
							{/* The results */}
							{results && (
								<div className="text-content space-y-4">
									<h2 className="mb-4 text-lg font-bold xl:text-xl">What were the results?</h2>
									<div className="content">{parse(results)}</div>
								</div>
							)}
							{/* The big picture */}
							{bigpicture && (
								<div className="text-content space-y-4">
									<h2 className="mb-4 text-lg font-bold xl:text-xl">The big picture</h2>
									<div className="content">{parse(bigpicture)}</div>
								</div>
							)}

							{additional && (
								<div className="text-content space-y-4">
									<h2 className="mb-4 text-lg font-bold xl:text-xl">
										Anything else I need to know?
									</h2>
									<div className="content">{parse(additional)}</div>
								</div>
							)}
						</>
					)}

					<AccessCheck feature={["study_summaries"]}>
						<Alert variant="premium">
							Every month we summarize over 150 of the most noteworthy health and nutrition studies.
							Other health categories related to this summary include:
							<ul className="list-disc py-4 pl-5 sm:pl-10">
								{categories?.map(
									(item: {
										id: React.Key;
										slug: any;
										name:
											| string
											| number
											| boolean
											| React.ReactElement<any, string | React.JSXElementConstructor<any>>
											| Iterable<React.ReactNode>
											| React.ReactPortal;
										tooltip: string;
									}) => (
										<li key={item.id}>
											<Link href={`/research-feed/filter/?filter=categories&value=${item.slug}`}>
												<span>
													<Tooltip>
														<TooltipTrigger asChild>
															<p className="inline-block text-examine-purple-400 hover:underline">
																{item.name}
															</p>
														</TooltipTrigger>
														<TooltipContent
															variant="overview"
															className="[@media(max-width:768px)]:hidden"
														>
															{parse(item.tooltip)}
														</TooltipContent>
													</Tooltip>
												</span>
											</Link>
										</li>
									)
								)}
							</ul>
							<SaleWrapper
								cta={
									<FreeTrialWrapper>
										<ExaminePlusLink
											trackDetails={{
												trackFor: "trial",
												label: "Try Examine+ for free",
												location: "SS - bottom",
											}}
											className="text-examine-purple-400 hover:underline"
										>
											Try Examine+ for free
										</ExaminePlusLink>
									</FreeTrialWrapper>
								}
							>
								<ExaminePlusLink
									trackDetails={{
										trackFor: "sale",
										label: `Get Examine+ for ${percentOff} off during our ${saleLabel}`,
										location: "SS - bottom",
									}}
									className="text-examine-purple-400 hover:underline"
								>
									Get Examine+ for {percentOff} off during our {saleLabel}
								</ExaminePlusLink>
							</SaleWrapper>{" "}
							to view the latest research in 26 health categories and the entire Study Summaries
							archive, access our{" "}
							<Link href="/guides/" className="text-examine-purple-400 hover:underline ">
								Supplement Guides
							</Link>
							, and unlock the Examine Database. Plus, earn continuing education credits!
							{!user ? (
								<>
									<br />
									<br />
									<NewsletterOptin>
										<>
											<strong>
												<a className="cursor-pointer text-examine-purple-400 hover:underline">
													Get free weekly updates on what’s new at Examine
												</a>
											</strong>
											.
										</>
									</NewsletterOptin>
								</>
							) : (
								""
							)}
						</Alert>
					</AccessCheck>

					<p className="mt-5 italic">
						This Study Summary was published on{" "}
						{created_date && format(new Date(created_date), "MMMM d',' yyyy")}.
					</p>

					<CitationSection citation={citation} />

					{Object.entries(references).length ? (
						<div id="references">
							<h2 className="mb-4 text-lg font-bold xl:text-xl">References</h2>
							<ol className="relative ml-5 w-full list-decimal space-y-6 py-4">
								{Object.entries(references)?.map(([id, value]: any, idx) => (
									<li key={idx} id={`ref-${value.ref_id}`} className="scroll-mt-28">
										<Citation citation={value} />
									</li>
								))}
							</ol>
						</div>
					) : null}
					<StudySummaryActionBar
						showFavorite={!!user}
						headline={headline}
						id={id}
						isFavorite={false}
					/>
				</div>
			) : (
				<div className="max-w-4xl space-y-7 bg-white pt-4 lg:pt-7 xl:pb-24">
					{background && (
						<div className="text-content space-y-4">
							<h2 className="mb-4 text-lg font-bold xl:text-xl">Background</h2>
							<div className="content">{parse(background)}</div>
						</div>
					)}
					{study && (
						<div className="text-content space-y-4">
							<h2 className="mb-4 text-lg font-bold xl:text-xl">The study</h2>
							<div className="content">{parse(study)}</div>
						</div>
					)}
					{requirelogin && !user ? (
						<div className="relative xl:text-xl xl:leading-9">
							<LockBadgeOverlay
								size={10}
								text={<SSOverlayCta />}
								className="!top-[10%] w-full !translate-y-0 [@media(min-width:500px)]:w-auto"
							/>
							<div className="pointer-events-none select-none space-y-7 blur-sm">
								{results && (
									<div className="text-content space-y-4">
										<h2 className="mb-4 text-lg font-bold xl:text-xl">The results</h2>
										<div className="content">{parse(replaceWordsWithSameLength(results))}</div>
									</div>
								)}

								{additional && (
									<div className="text-content space-y-4">
										<h2 className="mb-4 text-lg font-bold xl:text-xl">Note</h2>
										<div className="content">{parse(replaceWordsWithSameLength(additional))}</div>
									</div>
								)}

								{bigpicture && (
									<div className="text-content space-y-4">
										<h2 className="mb-4 text-lg font-bold xl:text-xl">The big picture</h2>
										<div className="content">{parse(replaceWordsWithSameLength(bigpicture))}</div>
									</div>
								)}
							</div>
						</div>
					) : (
						<>
							{results && (
								<div className="text-content space-y-4">
									<h2 className="mb-4 text-lg font-bold xl:text-xl">The results</h2>
									<div className="content">{parse(results)}</div>
								</div>
							)}
							{additional && (
								<div className="text-content space-y-4">
									<h2 className="mb-4 text-lg font-bold xl:text-xl">Note</h2>
									<div className="content">{parse(additional)}</div>
								</div>
							)}
							{bigpicture && (
								<div className="text-content space-y-4">
									<h2 className="mb-4 text-lg font-bold xl:text-xl">The big picture</h2>
									<div className="content">{parse(bigpicture)}</div>
								</div>
							)}
						</>
					)}

					<AccessCheck feature={["study_summaries"]}>
						<Alert variant="premium">
							Every month we summarize over 150 of the most noteworthy health and nutrition studies.
							Other health categories related to this summary include:
							<ul className="list-disc py-4 pl-5 sm:pl-10">
								{categories?.map(
									(item: {
										id: React.Key;
										slug: any;
										name:
											| string
											| number
											| boolean
											| React.ReactElement<any, string | React.JSXElementConstructor<any>>
											| Iterable<React.ReactNode>
											| React.ReactPortal;
										tooltip: string;
									}) => (
										<li key={item.id}>
											<Link href={`/research-feed/filter/?filter=categories&value=${item.slug}`}>
												<span>
													<Tooltip>
														<TooltipTrigger asChild>
															<p className="inline-block text-examine-purple-400 hover:underline">
																{item.name}
															</p>
														</TooltipTrigger>
														<TooltipContent
															variant="overview"
															className="[@media(max-width:768px)]:hidden"
														>
															{parse(item.tooltip)}
														</TooltipContent>
													</Tooltip>
												</span>
											</Link>
										</li>
									)
								)}
							</ul>
							<ExaminePlusLink
								trackDetails={{ label: "Try Examine+ for free", location: "SS - bottom" }}
								className="text-examine-purple-400 hover:underline"
							>
								Try Examine+ for free
							</ExaminePlusLink>{" "}
							to view the latest research in 25 health categories and the entire Study Summaries
							archive, access our{" "}
							<Link href="/guides/" className="text-examine-purple-400 hover:underline ">
								Supplement Guides
							</Link>
							, and unlock the Examine Database. Plus, earn continuing education credits!
							{!user ? (
								<>
									<br />
									<br />
									<NewsletterOptin>
										<>
											<strong>
												<a className="cursor-pointer text-examine-purple-400 hover:underline">
													Get free weekly updates on what’s new at Examine
												</a>
											</strong>
											.
										</>
									</NewsletterOptin>
								</>
							) : (
								""
							)}
						</Alert>
					</AccessCheck>

					<CitationSection citation={citation} />

					<p className="mt-5 italic">
						This Study Summary was published on{" "}
						{created_date && format(new Date(created_date), "MMMM d',' yyyy")}.
					</p>
					{Object.entries(references).length ? (
						<div id="references">
							<h2 className="mb-4 text-lg font-bold xl:text-xl">References</h2>
							<ol className="relative ml-5 w-full list-decimal space-y-6 py-4">
								{Object.entries(references)?.map(([id, value]: any, idx) => (
									<li key={idx} id={`ref-${value.ref_id}`} className="scroll-mt-28">
										<a href={`#ref-link-${value.ref_id}`} className="px-2 text-primary underline">
											^
										</a>
										<span className="pr-2">{value.authors}</span>
										<a
											className="text-primary hover:underline"
											href={value.url}
											target="_blank"
											rel="noreferrer"
										>
											{value.title}
										</a>
										<span className="px-2">{value.journal}.</span>
										<span>({value.published})</span>
									</li>
								))}
							</ol>
						</div>
					) : null}
					<StudySummaryActionBar
						showFavorite={!!user}
						headline={headline}
						id={id}
						isFavorite={false}
					/>
				</div>
			)}
		</ClientOnly>
	);
}
